/**
 * Voice AI — Agent One Guardrails Tab
 * Configures safety guardrails for the Agent One push-to-talk assistant.
 * Only visible when agent.isAgentOne === true.
 * Dispatches: save, agent-changed
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { agentStyles } from '../../styles/agent-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { authService } from '../../services/auth-service.js';

// Voice tool definitions with metadata
const VOICE_TOOLS = [
  { name: 'check_availability', label: 'Check Availability', type: 'read', desc: 'Query room/site availability for dates' },
  { name: 'get_site_filters', label: 'Get Site Filters', type: 'read', desc: 'Fetch categories, amenities, equipment types' },
  { name: 'get_room_details', label: 'Get Room Details', type: 'read', desc: 'Detailed pricing and add-on options' },
  { name: 'lookup_customer', label: 'Lookup Customer', type: 'read', desc: 'Find customer by phone or email' },
  { name: 'list_coupons', label: 'List Coupons', type: 'read', desc: 'Get active coupon codes and discounts' },
  { name: 'apply_coupon', label: 'Apply Coupon', type: 'read', desc: 'Validate coupon and calculate discount' },
  { name: 'create_reservation', label: 'Create Reservation', type: 'write', desc: 'Create a confirmed booking' },
  { name: 'charge_card', label: 'Charge Card', type: 'write', desc: 'Charge a saved payment card' },
  { name: 'send_payment_link', label: 'Send Payment Link', type: 'write', desc: 'SMS a secure payment link' },
  { name: 'end_call', label: 'End Call', type: 'control', desc: 'Terminate the phone call' },
];

const MODE_PRESETS = {
  informational: {
    label: 'Informational',
    icon: 'fa-book-open',
    desc: 'Read-only — answer questions, look up data. Cannot create bookings or process payments.',
    tools: ['check_availability', 'get_site_filters', 'get_room_details', 'lookup_customer', 'list_coupons', 'apply_coupon', 'end_call'],
  },
  operational: {
    label: 'Operational',
    icon: 'fa-briefcase',
    desc: 'Can read data AND perform booking operations — create reservations, send payment links.',
    tools: ['check_availability', 'get_site_filters', 'get_room_details', 'lookup_customer', 'list_coupons', 'apply_coupon', 'create_reservation', 'send_payment_link', 'end_call'],
  },
  full: {
    label: 'Full Access',
    icon: 'fa-shield-exclamation',
    desc: 'All voice tools + MCP tools enabled with configured restrictions. Use with caution.',
    tools: VOICE_TOOLS.map(t => t.name),
  },
};

const DEFAULT_GUARDRAILS = {
  mode: 'informational',
  allowed_tools: MODE_PRESETS.informational.tools,
  blocked_tools: [],
  mcp_read_only: true,
  mcp_db_read_only: true,
  mcp_block_code_modify: true,
  mcp_allowed_paths: [],
  mcp_blocked_operations: ['file_write', 'file_delete', 'code_modify', 'schema_alter', 'insert', 'update', 'delete', 'drop', 'alter', 'truncate'],
  custom_rules: [
    'Never modify, create, or delete any files on the system',
    'Never execute INSERT, UPDATE, DELETE, ALTER, or DROP SQL statements',
    'Only answer questions about the system — never attempt to fix issues directly',
  ],
  escalation_message: 'I can help you understand the issue, but I\'m not able to make changes to the system. Please contact your developer for fixes.',
};

export class AgentGuardrailsTab extends LitElement {
  static properties = {
    agent: { type: Object },
    saving: { type: Boolean },
    _voiceTools: { type: Array, state: true },
    _voiceToolsLoading: { type: Boolean, state: true },
    _strictMode: { type: Boolean, state: true },
    _expandedCategories: { type: Object, state: true },
    _toolSearch: { type: String, state: true },
  };

  static styles = [designSystem, componentStyles, agentStyles, css`
    .guardrails-section {
      background: var(--color-bg);
      border: 1px solid var(--color-border);
      border-radius: 12px;
      padding: 20px 24px;
      margin-bottom: 20px;
    }
    .guardrails-section-title {
      font-size: 15px;
      font-weight: 600;
      color: var(--color-text-primary);
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .guardrails-section-desc {
      font-size: 13px;
      color: var(--color-text-tertiary);
      margin-bottom: 16px;
    }

    .mode-cards {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
    }
    .mode-card {
      padding: 16px;
      border: 2px solid var(--color-border);
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.15s ease;
      text-align: center;
    }
    .mode-card:hover {
      border-color: #bbd7b5;
      background: #fafdf9;
    }
    .mode-card.active {
      border-color: #46A546;
      background: #F7FAF4;
      box-shadow: 0 0 0 3px rgba(70, 165, 70, 0.1);
    }
    .mode-card-icon {
      font-size: 24px;
      margin-bottom: 8px;
      color: var(--color-text-tertiary);
    }
    .mode-card.active .mode-card-icon { color: #2C6C11; }
    .mode-card-label {
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 4px;
    }
    .mode-card-desc {
      font-size: 12px;
      color: var(--color-text-tertiary);
      line-height: 1.4;
    }

    .tool-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
    }
    .tool-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border: 1px solid var(--color-border);
      border-radius: 8px;
      transition: all 0.15s ease;
    }
    .tool-item:hover { background: #fafdf9; }
    .tool-item.enabled { border-color: #bbd7b5; background: #fafdf9; }
    .tool-item.disabled { opacity: 0.5; }
    .tool-checkbox {
      width: 18px;
      height: 18px;
      border-radius: 4px;
      border: 2px solid #d1d5db;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      flex-shrink: 0;
      transition: all 0.15s ease;
    }
    .tool-checkbox.checked {
      background: #46A546;
      border-color: #46A546;
      color: #fff;
    }
    .tool-info { flex: 1; min-width: 0; }
    .tool-name {
      font-size: 13px;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .tool-desc { font-size: 11px; color: var(--color-text-tertiary); margin-top: 2px; }
    .tool-badge {
      font-size: 9px;
      font-weight: 600;
      padding: 1px 6px;
      border-radius: 100px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .tool-badge.read { background: #e8f4fc; color: #1e6a8a; }
    .tool-badge.write { background: #fef2f2; color: #a43333; }
    .tool-badge.control { background: #F5F2F0; color: #6b7280; }

    .mcp-toggles {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .mcp-toggle-row {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      background: #FDFCFB;
      border: 1px solid var(--color-border);
      border-radius: 8px;
    }
    .mcp-toggle-info { flex: 1; }
    .mcp-toggle-label { font-size: 13px; font-weight: 500; }
    .mcp-toggle-hint { font-size: 11px; color: var(--color-text-tertiary); margin-top: 2px; }

    .rules-textarea {
      width: 100%;
      min-height: 120px;
      padding: 12px 16px;
      border: 1px solid var(--color-border);
      border-radius: 10px;
      font-size: 13px;
      font-family: 'JetBrains Mono', monospace;
      line-height: 1.6;
      resize: vertical;
      box-sizing: border-box;
      transition: all 0.15s ease;
    }
    .rules-textarea:focus {
      border-color: #46A546;
      box-shadow: 0 0 0 3px rgba(70, 165, 70, 0.1);
      outline: none;
    }
    .escalation-input {
      width: 100%;
      padding: 12px 16px;
      border: 1px solid var(--color-border);
      border-radius: 10px;
      font-size: 13px;
      box-sizing: border-box;
      transition: all 0.15s ease;
    }
    .escalation-input:focus {
      border-color: #46A546;
      box-shadow: 0 0 0 3px rgba(70, 165, 70, 0.1);
      outline: none;
    }

    /* Voice Tools Section */
    .vt-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 16px;
    }
    .vt-search {
      padding: 8px 12px;
      border: 1px solid var(--color-border);
      border-radius: 8px;
      font-size: 13px;
      width: 220px;
      box-sizing: border-box;
    }
    .vt-search:focus { border-color: #46A546; outline: none; box-shadow: 0 0 0 3px rgba(70,165,70,0.1); }
    .vt-strict-row {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 16px;
      background: #FFFBF0;
      border: 1px solid #E8DFC8;
      border-radius: 10px;
      margin-bottom: 16px;
    }
    .vt-strict-info { flex: 1; }
    .vt-strict-label { font-size: 13px; font-weight: 600; color: #8B6914; }
    .vt-strict-hint { font-size: 11px; color: #A48E33; margin-top: 2px; }
    .vt-category {
      border: 1px solid var(--color-border);
      border-radius: 10px;
      margin-bottom: 10px;
      overflow: hidden;
    }
    .vt-category-header {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 16px;
      cursor: pointer;
      background: #FDFCFB;
      user-select: none;
      transition: background 0.1s ease;
    }
    .vt-category-header:hover { background: #F5F2F0; }
    .vt-category-chevron {
      font-size: 12px;
      color: var(--color-text-tertiary);
      transition: transform 0.2s ease;
      width: 16px;
      text-align: center;
    }
    .vt-category-chevron.open { transform: rotate(90deg); }
    .vt-category-name {
      font-size: 14px;
      font-weight: 600;
      flex: 1;
      text-transform: capitalize;
    }
    .vt-category-count {
      font-size: 11px;
      color: var(--color-text-tertiary);
      background: var(--color-bg-secondary);
      padding: 2px 8px;
      border-radius: 100px;
    }
    .vt-category-actions {
      display: flex;
      gap: 8px;
      font-size: 11px;
    }
    .vt-category-actions a {
      color: var(--color-primary);
      cursor: pointer;
      text-decoration: none;
    }
    .vt-category-actions a:hover { text-decoration: underline; }
    .vt-category-body {
      padding: 8px 12px 12px;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 6px;
    }
    .vt-tool {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 10px;
      border: 1px solid transparent;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.1s ease;
    }
    .vt-tool:hover { background: #fafdf9; border-color: #e8e3db; }
    .vt-tool.on { border-color: #bbd7b5; background: #fafdf9; }
    .vt-tool-check {
      width: 16px; height: 16px;
      border-radius: 4px;
      border: 2px solid #d1d5db;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 10px;
      transition: all 0.15s ease;
    }
    .vt-tool-check.on { background: #46A546; border-color: #46A546; color: #fff; }
    .vt-tool-name { font-size: 12px; font-weight: 500; flex: 1; }
    .vt-risk {
      font-size: 9px;
      font-weight: 600;
      padding: 1px 6px;
      border-radius: 100px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    .vt-risk.low { background: #e8f4fc; color: #1e6a8a; }
    .vt-risk.medium { background: #fef7e0; color: #8B6914; }
    .vt-risk.high { background: #fef2f2; color: #a43333; }
    .vt-empty {
      text-align: center;
      padding: 24px;
      color: var(--color-text-tertiary);
      font-size: 13px;
    }

    @media (max-width: 768px) {
      .mode-cards { grid-template-columns: 1fr; }
      .tool-grid { grid-template-columns: 1fr; }
      .vt-category-body { grid-template-columns: 1fr; }
      .vt-header { flex-wrap: wrap; }
      .vt-search { width: 100%; }
    }
  `];

  constructor() {
    super();
    this._voiceTools = [];
    this._voiceToolsLoading = false;
    this._strictMode = true;
    this._expandedCategories = {};
    this._toolSearch = '';
    this._lastAgentId = null;
  }

  updated(changed) {
    if (changed.has('agent') && this.agent?.id !== this._lastAgentId) {
      this._lastAgentId = this.agent?.id;
      this._loadVoiceTools();
    }
  }

  async _loadVoiceTools() {
    if (!this.agent?.id) return;
    this._voiceToolsLoading = true;
    try {
      const resp = await authService.apiFetch(`/voice/php/api/agent-tools.php?agent_id=${this.agent.id}`);
      const data = await resp.json();
      if (data.success) {
        this._voiceTools = data.tools || [];
        this._strictMode = !!data.strict_mode;
        // Auto-expand first category
        const cats = [...new Set(this._voiceTools.map(t => t.category))];
        if (cats.length > 0 && Object.keys(this._expandedCategories).length === 0) {
          this._expandedCategories = { [cats[0]]: true };
        }
      }
    } catch (e) {
      console.error('Failed to load voice tools:', e);
    }
    this._voiceToolsLoading = false;
  }

  _toggleCategory(cat) {
    this._expandedCategories = {
      ...this._expandedCategories,
      [cat]: !this._expandedCategories[cat],
    };
  }

  _toggleVoiceTool(toolKey) {
    this._voiceTools = this._voiceTools.map(t =>
      t.tool_key === toolKey ? { ...t, is_enabled: !t.is_enabled } : t
    );
  }

  _setCategoryAll(cat, enabled) {
    this._voiceTools = this._voiceTools.map(t =>
      t.category === cat ? { ...t, is_enabled: enabled } : t
    );
  }

  _toggleStrictMode() {
    this._strictMode = !this._strictMode;
  }

  async _saveVoiceTools() {
    if (!this.agent?.id) return;
    try {
      await authService.apiFetch('/voice/php/api/agent-tools.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_id: this.agent.id,
          strict_mode: this._strictMode ? 1 : 0,
          tools: this._voiceTools.map(t => ({
            tool_key: t.tool_key,
            is_enabled: t.is_enabled ? 1 : 0,
          })),
        }),
      });
      this.dispatchEvent(new CustomEvent('toast', {
        detail: { message: 'Voice tools saved', type: 'success' },
        bubbles: true, composed: true,
      }));
    } catch (e) {
      this.dispatchEvent(new CustomEvent('toast', {
        detail: { message: 'Failed to save voice tools', type: 'error' },
        bubbles: true, composed: true,
      }));
    }
  }

  _renderVoiceToolsSection() {
    const tools = this._voiceTools;
    const search = this._toolSearch.toLowerCase();

    // Filter by search
    const filtered = search
      ? tools.filter(t => t.name.toLowerCase().includes(search) || t.tool_key.toLowerCase().includes(search) || t.description.toLowerCase().includes(search))
      : tools;

    // Group by category
    const categories = {};
    for (const t of filtered) {
      if (!categories[t.category]) categories[t.category] = [];
      categories[t.category].push(t);
    }
    const catNames = Object.keys(categories).sort();

    if (this._voiceToolsLoading) {
      return html`<div class="vt-empty"><i class="fa-sharp fa-light fa-spinner-third fa-spin"></i> Loading tools...</div>`;
    }

    if (tools.length === 0) {
      return html`<div class="vt-empty">No voice tools configured. Tools are added to the system database.</div>`;
    }

    const enabledCount = tools.filter(t => t.is_enabled).length;

    return html`
      <!-- Strict Mode Toggle -->
      <div class="vt-strict-row">
        <div class="vt-strict-info">
          <div class="vt-strict-label">
            <i class="fa-sharp fa-light fa-lock-keyhole" style="margin-right:4px;"></i>
            Strict Mode
          </div>
          <div class="vt-strict-hint">When ON, the AI can ONLY use tools from this list. When OFF, the AI can also perform ad-hoc operations.</div>
        </div>
        <div class="toggle ${this._strictMode ? 'active' : ''}" @click=${() => this._toggleStrictMode()}>
          <div class="toggle-slider"></div>
        </div>
      </div>

      <!-- Search + Stats -->
      <div class="vt-header">
        <div style="font-size:12px;color:var(--color-text-tertiary);">
          ${enabledCount} of ${tools.length} tools enabled across ${catNames.length} categories
        </div>
        <input type="text" class="vt-search" placeholder="Search tools..."
          .value=${this._toolSearch}
          @input=${e => { this._toolSearch = e.target.value; }}
        />
      </div>

      <!-- Category Accordions -->
      ${catNames.map(cat => {
        const catTools = categories[cat];
        const expanded = this._expandedCategories[cat];
        const enabledInCat = catTools.filter(t => t.is_enabled).length;
        const allEnabled = enabledInCat === catTools.length;

        return html`
          <div class="vt-category">
            <div class="vt-category-header" @click=${() => this._toggleCategory(cat)}>
              <div class="vt-category-chevron ${expanded ? 'open' : ''}">
                <i class="fa-sharp fa-regular fa-chevron-right"></i>
              </div>
              <div class="vt-category-name">${cat}</div>
              <div class="vt-category-count">${enabledInCat}/${catTools.length}</div>
              <div class="vt-category-actions" @click=${e => e.stopPropagation()}>
                <a @click=${() => this._setCategoryAll(cat, true)}>All</a>
                <a @click=${() => this._setCategoryAll(cat, false)}>None</a>
              </div>
            </div>
            ${expanded ? html`
              <div class="vt-category-body">
                ${catTools.map(t => html`
                  <div class="vt-tool ${t.is_enabled ? 'on' : ''}" @click=${() => this._toggleVoiceTool(t.tool_key)} title="${t.description}">
                    <div class="vt-tool-check ${t.is_enabled ? 'on' : ''}">
                      ${t.is_enabled ? html`<i class="fa-sharp fa-regular fa-check"></i>` : ''}
                    </div>
                    <div class="vt-tool-name">${t.name}</div>
                    <span class="vt-risk ${t.risk_level || 'low'}">${t.risk_level || 'low'}</span>
                  </div>
                `)}
              </div>
            ` : ''}
          </div>
        `;
      })}

      <button class="btn btn-primary btn-sm" style="margin-top:12px;" @click=${() => this._saveVoiceTools()}>
        <i class="fa-sharp fa-light fa-floppy-disk-pen"></i> Save Tool Configuration
      </button>
    `;
  }

  _getGuardrails() {
    return { ...DEFAULT_GUARDRAILS, ...(this.agent?.guardrails || {}) };
  }

  _updateGuardrails(patch) {
    const guardrails = { ...this._getGuardrails(), ...patch };
    const agent = { ...this.agent, guardrails };
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent } }));
  }

  // Pipeline override helpers (stored on agent.pipelineOverrides)
  _getOverride(key, defaultVal) {
    return this.agent?.pipelineOverrides?.[key] ?? defaultVal;
  }

  _setOverride(key, value) {
    const overrides = { ...(this.agent?.pipelineOverrides || {}), [key]: value };
    const agent = { ...this.agent, pipelineOverrides: overrides };
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent } }));
  }

  _setMode(mode) {
    const preset = MODE_PRESETS[mode];
    const patch = {
      mode,
      allowed_tools: [...preset.tools],
    };
    // Full Access mode unlocks MCP file changes
    if (mode === 'full') {
      patch.mcp_read_only = false;
      patch.mcp_block_code_modify = false;
      patch.mcp_db_read_only = false;
    } else {
      // Non-full modes enforce read-only safety
      patch.mcp_read_only = true;
      patch.mcp_block_code_modify = true;
      patch.mcp_db_read_only = true;
    }
    this._updateGuardrails(patch);
  }

  _toggleTool(toolName) {
    const g = this._getGuardrails();
    const tools = [...(g.allowed_tools || [])];
    const idx = tools.indexOf(toolName);
    if (idx >= 0) {
      tools.splice(idx, 1);
    } else {
      tools.push(toolName);
    }
    // Switch to custom mode if tools don't match any preset
    let mode = 'custom';
    for (const [key, preset] of Object.entries(MODE_PRESETS)) {
      if (tools.length === preset.tools.length && tools.every(t => preset.tools.includes(t))) {
        mode = key;
        break;
      }
    }
    this._updateGuardrails({ allowed_tools: tools, mode });
  }

  _collectAndSave() {
    const root = this.shadowRoot;
    const g = this._getGuardrails();

    // Collect custom rules from textarea
    const rulesEl = root.querySelector('.rules-textarea');
    if (rulesEl) {
      g.custom_rules = rulesEl.value.split('\n').map(l => l.trim()).filter(Boolean);
    }

    // Collect escalation message
    const escEl = root.querySelector('.escalation-input');
    if (escEl) {
      g.escalation_message = escEl.value.trim();
    }

    const agent = { ...this.agent, guardrails: g };
    this.dispatchEvent(new CustomEvent('save', { detail: { agent } }));

    // Also save voice tools + strict mode if tools are loaded
    if (this._voiceTools.length > 0) {
      this._saveVoiceTools();
    }
  }

  render() {
    const agent = this.agent;
    if (!agent) return html``;
    const g = this._getGuardrails();

    return html`
      ${iconStyles}

      <!-- Section 1: Operation Mode -->
      <div class="guardrails-section">
        <div class="guardrails-section-title">
          <i class="fa-sharp fa-light fa-shield-halved" style="color:#2C6C11;"></i>
          Operation Mode
        </div>
        <div class="guardrails-section-desc">Choose what Agent One is allowed to do. This controls which tools are available.</div>

        <div class="mode-cards">
          ${Object.entries(MODE_PRESETS).map(([key, preset]) => html`
            <div
              class="mode-card ${g.mode === key ? 'active' : ''}"
              @click=${() => this._setMode(key)}
            >
              <div class="mode-card-icon"><i class="fa-sharp fa-light ${preset.icon}"></i></div>
              <div class="mode-card-label">${preset.label}</div>
              <div class="mode-card-desc">${preset.desc}</div>
            </div>
          `)}
        </div>
      </div>

      <!-- Section 2: Voice Tools -->
      <div class="guardrails-section">
        <div class="guardrails-section-title">
          <i class="fa-sharp fa-light fa-wrench" style="color:#A48E33;"></i>
          Voice Tools
        </div>
        <div class="guardrails-section-desc">Individual control over which voice tools the agent can use. Changes here may override the mode preset.</div>

        <div class="tool-grid">
          ${VOICE_TOOLS.map(tool => {
            const enabled = (g.allowed_tools || []).includes(tool.name);
            return html`
              <div class="tool-item ${enabled ? 'enabled' : 'disabled'}" @click=${() => this._toggleTool(tool.name)}>
                <div class="tool-checkbox ${enabled ? 'checked' : ''}">
                  ${enabled ? html`<i class="fa-solid fa-check" style="font-size:11px;"></i>` : ''}
                </div>
                <div class="tool-info">
                  <div class="tool-name">
                    ${tool.label}
                    <span class="tool-badge ${tool.type}">${tool.type}</span>
                  </div>
                  <div class="tool-desc">${tool.desc}</div>
                </div>
              </div>
            `;
          })}
        </div>
      </div>

      <!-- Section 3: System Voice Tools (moved up from Section 7) -->
      <div class="guardrails-section">
        <div class="guardrails-section-title">
          <i class="fa-sharp fa-light fa-toolbox" style="color:#5B8A72;"></i>
          System Voice Tools
        </div>
        <div class="guardrails-section-desc">
          Registered system operations the AI can execute directly. Each tool maps to an actual system function — same as clicking it in the admin panel. Toggle tools on/off per agent.
        </div>

        ${this._renderVoiceToolsSection()}
      </div>

      <!-- Section 4: MCP Safety -->
      <div class="guardrails-section">
        <div class="guardrails-section-title">
          <i class="fa-sharp fa-light fa-lock" style="color:#C75050;"></i>
          MCP Safety
        </div>
        <div class="guardrails-section-desc">Controls for the Local Claude MCP provider. These restrict what Claude can do when using MCP tools on your system.</div>

        <div class="mcp-toggles">
          <div class="mcp-toggle-row">
            <div class="mcp-toggle-info">
              <div class="mcp-toggle-label">Read-only filesystem access</div>
              <div class="mcp-toggle-hint">Claude can read files but cannot create, modify, or delete any files on the system.</div>
            </div>
            <div
              class="toggle ${g.mcp_read_only !== false ? 'active' : ''}"
              @click=${() => this._updateGuardrails({ mcp_read_only: !(g.mcp_read_only !== false) })}
            ></div>
          </div>
          <div class="mcp-toggle-row">
            <div class="mcp-toggle-info">
              <div class="mcp-toggle-label">Read-only database queries</div>
              <div class="mcp-toggle-hint">Only SELECT statements allowed. INSERT, UPDATE, DELETE, ALTER, DROP are blocked.</div>
            </div>
            <div
              class="toggle ${g.mcp_db_read_only !== false ? 'active' : ''}"
              @click=${() => this._updateGuardrails({ mcp_db_read_only: !(g.mcp_db_read_only !== false) })}
            ></div>
          </div>
          <div class="mcp-toggle-row" style="border-color:#e8c4c4;">
            <div class="mcp-toggle-info">
              <div class="mcp-toggle-label" style="color:#a43333;">
                <i class="fa-solid fa-triangle-exclamation" style="margin-right:4px;"></i>
                Block code file modifications
              </div>
              <div class="mcp-toggle-hint">Prevents Claude from modifying PHP, JS, CSS, or any source code files. <strong>Strongly recommended.</strong></div>
            </div>
            <div
              class="toggle ${g.mcp_block_code_modify !== false ? 'active' : ''}"
              @click=${() => this._updateGuardrails({ mcp_block_code_modify: !(g.mcp_block_code_modify !== false) })}
            ></div>
          </div>
        </div>
      </div>

      <!-- Section 4: Custom Rules -->
      <div class="guardrails-section">
        <div class="guardrails-section-title">
          <i class="fa-sharp fa-light fa-list-check" style="color:var(--color-text-tertiary);"></i>
          Custom Rules
        </div>
        <div class="guardrails-section-desc">Additional instructions injected into the agent's system prompt. One rule per line. These are mandatory and cannot be bypassed.</div>

        <textarea
          class="rules-textarea"
          .value=${(g.custom_rules || []).join('\n')}
          placeholder="One rule per line, e.g.:&#10;Never modify source code files&#10;Only answer questions about bookings and revenue"
        ></textarea>
      </div>

      <!-- Section 5: Escalation Message -->
      <div class="guardrails-section">
        <div class="guardrails-section-title">
          <i class="fa-sharp fa-light fa-message-exclamation" style="color:#1e6a8a;"></i>
          Escalation Message
        </div>
        <div class="guardrails-section-desc">What the agent says when a user asks for something that's blocked by guardrails.</div>

        <input
          type="text"
          class="escalation-input"
          .value=${g.escalation_message || ''}
          placeholder="I can help you understand the issue, but I'm not able to make changes to the system."
        />
      </div>

      <!-- Section 6: Audio Settings -->
      <div class="guardrails-section">
        <div class="guardrails-section-title">
          <i class="fa-sharp fa-light fa-waveform-lines" style="color:#5B8A72;"></i>
          Audio Settings
        </div>
        <div class="guardrails-section-desc">Fine-tune speech recognition sensitivity for Agent One calls. These override the global pipeline defaults for this agent.</div>

        <div style="display:flex;flex-direction:column;gap:16px;">
          <div>
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
              <label style="font-size:13px;font-weight:500;">STT Silence Endpointing</label>
              <span style="font-size:12px;color:var(--color-text-tertiary);font-variant-numeric:tabular-nums;">${this._getOverride('stt_endpointing', 600)}ms</span>
            </div>
            <input type="range" min="200" max="1500" step="50"
              .value=${String(this._getOverride('stt_endpointing', 600))}
              @input=${e => this._setOverride('stt_endpointing', parseInt(e.target.value))}
              style="width:100%;"
            />
            <div style="font-size:11px;color:var(--color-text-tertiary);margin-top:4px;">How long to wait after silence before processing speech. Lower = faster response, higher = waits longer for pauses.</div>
          </div>

          <div>
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
              <label style="font-size:13px;font-weight:500;">Utterance End Silence</label>
              <span style="font-size:12px;color:var(--color-text-tertiary);font-variant-numeric:tabular-nums;">${this._getOverride('stt_utterance_end_ms', 1500)}ms</span>
            </div>
            <input type="range" min="1000" max="3000" step="100"
              .value=${String(this._getOverride('stt_utterance_end_ms', 1500))}
              @input=${e => this._setOverride('stt_utterance_end_ms', parseInt(e.target.value))}
              style="width:100%;"
            />
            <div style="font-size:11px;color:var(--color-text-tertiary);margin-top:4px;">Final silence before the turn is complete. Higher values let the caller pause without the AI jumping in.</div>
          </div>

          <div>
            <label style="font-size:13px;font-weight:500;display:block;margin-bottom:6px;">Barge-in Mode</label>
            <select class="form-select" style="width:100%;"
              .value=${this._getOverride('bargein_mode', 'allow')}
              @change=${e => this._setOverride('bargein_mode', e.target.value)}
            >
              <option value="allow" ?selected=${this._getOverride('bargein_mode', 'allow') === 'allow'}>Allow Interruptions</option>
              <option value="mute" ?selected=${this._getOverride('bargein_mode', 'allow') === 'mute'}>Mute During AI Speech</option>
              <option value="disabled" ?selected=${this._getOverride('bargein_mode', 'allow') === 'disabled'}>Disabled (No Interruptions)</option>
            </select>
            <div style="font-size:11px;color:var(--color-text-tertiary);margin-top:4px;">How the agent handles caller interruptions while speaking. "Allow" lets callers interrupt naturally.</div>
          </div>
        </div>
      </div>

      <div class="actions-footer">
        <div></div>
        <button class="btn btn-primary" ?disabled=${this.saving} @click=${() => this._collectAndSave()}>
          <i class="fa-sharp fa-light ${this.saving ? 'fa-spinner-third fa-spin' : 'fa-floppy-disk'}"></i>
          ${this.saving ? 'Saving...' : 'Save Guardrails'}
        </button>
      </div>
    `;
  }
}

customElements.define('agent-guardrails-tab', AgentGuardrailsTab);
